import { type APIMessage, formatRelative } from '@alternativamais/chat-core';
import { useState } from 'react';

import { avisarErro } from '../../../componentes/Avisos';
import { Icone } from '../../../componentes/Icone';
import { useAgora } from '../../../chat/ganchos';
import { useCliente, useEstado } from '../../../chat/contexto';
import { EmojiVisual } from '../SeletorEmoji';

function restante(expira: number, agora: number): string {
  const minutos = Math.max(1, Math.round((expira - agora) / 60_000));
  if (minutos < 60) return `${minutos} min restantes`;
  const horas = Math.round(minutos / 60);
  if (horas < 48) return `${horas} h restantes`;
  return `${Math.round(horas / 24)} dias restantes`;
}

/**
 * Enquete na mensagem (C27). Como o Discord: antes de votar, só as
 * respostas (dá para espiar os resultados); votou, encerrou ou é quem
 * criou, vê as porcentagens. Uma resposta vota no clique; várias marcam e
 * confirmam em "Votar".
 */
export function Enquete({ mensagem }: { mensagem: APIMessage }) {
  const cliente = useCliente();
  const enquete = mensagem.poll!;
  const agora = useAgora(60_000);
  const euId = useEstado((estado) => estado.me?.id);
  const contagens = enquete.results?.answer_counts ?? [];
  const meus = contagens.filter((item) => item.me_voted).map((item) => item.id);
  const votei = meus.length > 0;
  const expira = enquete.expiry ? new Date(enquete.expiry).getTime() : null;
  const encerrada =
    !!enquete.results?.is_finalized || (expira !== null && expira <= agora);
  const [espiar, setEspiar] = useState(false);
  const [marcadas, setMarcadas] = useState<number[]>([]);
  const [enviando, setEnviando] = useState(false);
  const total = contagens.reduce((soma, item) => soma + item.count, 0);
  const verResultado =
    encerrada || votei || espiar || mensagem.author.id === euId;
  const multipla = enquete.allow_multiselect;
  const vencedora = encerrada
    ? Math.max(0, ...contagens.map((item) => item.count))
    : -1;

  const votar = (ids: number[]) => {
    setEnviando(true);
    cliente.api
      .vote(mensagem.channel_id, mensagem.id, ids)
      .then(() => setMarcadas([]))
      .catch(avisarErro)
      .finally(() => setEnviando(false));
  };

  const id = `enquete-${mensagem.id}`;
  return (
    <section
      aria-labelledby={id}
      className="mt-1 flex w-[520px] max-w-full flex-col gap-3 rounded-xl bg-elevado p-4"
    >
      <div>
        <h3 id={id} className="m-0 text-[17px] leading-[1.35] font-bold">
          {enquete.question.text}
        </h3>
        <p className="mt-1 mb-0 text-[13px] text-texto-3">
          {encerrada
            ? 'Enquete encerrada'
            : multipla
              ? 'Selecione uma ou mais respostas'
              : 'Selecione uma resposta'}
        </p>
      </div>
      <div
        role={multipla ? 'group' : 'radiogroup'}
        aria-labelledby={id}
        className="flex flex-col gap-2"
      >
        {enquete.answers.map((resposta) => {
          const contagem = contagens.find(
            (item) => item.id === resposta.answer_id,
          );
          const votos = contagem?.count ?? 0;
          const parte = total ? Math.round((votos / total) * 100) : 0;
          const minha = !!contagem?.me_voted;
          const marcada = votei ? minha : marcadas.includes(resposta.answer_id);
          const ganhou = encerrada && votos > 0 && votos === vencedora;
          const texto = resposta.poll_media.text ?? '';
          const rotulo = verResultado
            ? `${texto}, ${parte}%, ${votos} ${votos === 1 ? 'voto' : 'votos'}${minha ? ', seu voto' : ''}`
            : texto;
          return (
            <button
              key={resposta.answer_id}
              type="button"
              role={multipla ? 'checkbox' : 'radio'}
              aria-checked={marcada}
              aria-label={rotulo}
              disabled={encerrada || enviando}
              onClick={() => {
                if (votei) return;
                if (!multipla) return votar([resposta.answer_id]);
                setMarcadas((atual) =>
                  atual.includes(resposta.answer_id)
                    ? atual.filter((item) => item !== resposta.answer_id)
                    : [...atual, resposta.answer_id],
                );
              }}
              className={`relative flex h-11 items-center gap-2.5 overflow-hidden rounded-lg border bg-chat px-3 text-left disabled:cursor-default ${
                marcada || ganhou ? 'border-destaque' : 'border-transparent'
              } ${votei || encerrada ? '' : 'hover:border-trilho'}`}
            >
              {verResultado && (
                <span
                  aria-hidden="true"
                  className="absolute inset-y-0 left-0 bg-[rgba(245,154,69,.22)] transition-[width]"
                  style={{ width: `${parte}%` }}
                />
              )}
              {marcada ? (
                <span
                  aria-hidden="true"
                  className={`relative flex size-5 shrink-0 items-center justify-center bg-destaque text-destaque-texto ${multipla ? 'rounded' : 'rounded-full'}`}
                >
                  <Icone nome="check" tamanho={14} />
                </span>
              ) : (
                !encerrada && (
                  <span
                    aria-hidden="true"
                    className={`relative size-5 shrink-0 border-2 border-trilho ${multipla ? 'rounded' : 'rounded-full'}`}
                  />
                )
              )}
              {resposta.poll_media.emoji && (
                <span className="relative">
                  <EmojiVisual
                    emoji={{
                      id: resposta.poll_media.emoji.id ?? null,
                      name: resposta.poll_media.emoji.name ?? null,
                    }}
                    tamanho={20}
                  />
                </span>
              )}
              <span
                className={`relative min-w-0 grow truncate text-[15px] ${marcada || ganhou ? 'font-semibold' : 'font-medium'}`}
              >
                {texto}
              </span>
              {ganhou && (
                <span className="relative text-xs font-semibold text-destaque">
                  Vencedora
                </span>
              )}
              {verResultado && (
                <>
                  <span className="relative text-[15px] font-bold">
                    {parte}%
                  </span>
                  <span className="relative w-14 text-right text-[13px] text-texto-2">
                    {votos} {votos === 1 ? 'voto' : 'votos'}
                  </span>
                </>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-[13px] text-texto-3">
        <span>
          {total} {total === 1 ? 'voto' : 'votos'}
        </span>
        <span aria-hidden="true">·</span>
        <span>
          {encerrada
            ? expira
              ? `Encerrada ${formatRelative(Math.min(expira, agora), new Date(agora))}`
              : 'Encerrada'
            : expira
              ? restante(expira, agora)
              : 'Sem prazo'}
        </span>
        {!encerrada && votei && (
          <>
            <span aria-hidden="true">·</span>
            <button
              type="button"
              disabled={enviando}
              onClick={() => votar([])}
              className="font-medium text-link hover:underline"
            >
              Remover voto
            </button>
          </>
        )}
        {!encerrada && !votei && mensagem.author.id !== euId && (
          <>
            <span aria-hidden="true">·</span>
            <button
              type="button"
              onClick={() => setEspiar(!espiar)}
              className="font-medium text-link hover:underline"
            >
              {espiar ? 'Esconder resultados' : 'Mostrar resultados'}
            </button>
          </>
        )}
        {multipla && !votei && !encerrada && (
          <button
            type="button"
            disabled={!marcadas.length || enviando}
            onClick={() => votar(marcadas)}
            className="ml-auto h-8 rounded-lg bg-destaque px-4 text-sm font-semibold text-destaque-texto disabled:opacity-50"
          >
            Votar
          </button>
        )}
      </div>
    </section>
  );
}

/** Resultado da enquete (tipo 46): a vencedora e o link para a enquete. */
export function ResultadoEnquete({
  mensagem,
  autor,
  aoVer,
}: {
  mensagem: APIMessage;
  autor: string;
  aoVer?: () => void;
}) {
  const campos = mensagem.embeds[0]?.fields ?? [];
  const campo = (nome: string) =>
    campos.find((item) => item.name === nome)?.value ?? '';
  const total = Number(campo('total_votes')) || 0;
  const votos = Number(campo('victor_answer_votes')) || 0;
  const vencedora = campo('victor_answer_text');
  const parte = total ? Math.round((votos / total) * 100) : 0;
  const pergunta = campo('poll_question_text');
  return (
    <div className="flex min-w-0 grow flex-col gap-2">
      <p className="m-0 text-[15px] text-texto-2">
        A enquete de{' '}
        <strong className="font-semibold text-texto">{autor}</strong> terminou:{' '}
        <span className="text-texto">{pergunta}</span>
      </p>
      <div className="flex w-[460px] max-w-full items-center gap-3 rounded-lg border border-borda bg-elevado p-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-destaque-suave text-destaque">
          <Icone nome={vencedora ? 'check' : 'enquete'} tamanho={18} />
        </span>
        <div className="flex min-w-0 grow flex-col gap-0.5">
          <span
            className="truncate text-[15px] font-semibold"
            title={vencedora}
          >
            {vencedora
              ? `${vencedora} (${parte}%)`
              : total
                ? 'Empate'
                : 'Ninguém votou'}
          </span>
          <span className="text-[13px] text-texto-3">
            {vencedora
              ? `Resposta vencedora · ${votos} ${votos === 1 ? 'voto' : 'votos'}`
              : `${total} ${total === 1 ? 'voto' : 'votos'}`}
          </span>
        </div>
        {aoVer && (
          <button
            type="button"
            onClick={aoVer}
            className="h-8 shrink-0 rounded-lg bg-ativo px-3 text-sm font-semibold hover:brightness-110"
          >
            Ver enquete
          </button>
        )}
      </div>
    </div>
  );
}
